<template>
  <div>
    <div v-if="loading" class="dot-opacity-loader">
      <span></span>
      <span></span>
      <span></span>
    </div>
    <div v-else-if="!previewValues.length" class="row justify-content-center">
      <h4 class="p-3">{{ $t('dashboards.noData') }}</h4>
    </div>
    <apexchart
      width="100%"
      height="350"
      :options="mainOptions"
      :series="mainSeries"
      class="chart2"
    ></apexchart>
    <apexchart
      width="100%"
      height="150"
      :options="previewOptions"
      :series="previewSeries"
      class="chart-bar"
    ></apexchart>
  </div>
</template>

<script>
import sensorValues from '@/api/boxinfo/sensorValues.gql';
import moment from 'moment';
import ru from 'apexcharts/dist/locales/ru.json';

export default {
  name: 'BoxSensorPlot',
  components: {
    apexchart: () => import('vue-apexcharts'),
  },
  props: {
    sensorId: Number,
    period: Object,
  },
  apollo: {
    sensorData: {
      query: sensorValues,
      variables() {
        const selection = this.selectionInner || this.selection;
        return {
          id: this.sensorId,
          startTime: moment(selection.min).add(-2, 'h').format(),
          endTime: moment(selection.max).add(-2, 'h').format(),
          sampleCount: 1000,
          aggregation: this.chartType == 'stepLine' ? 'MAX' : null,
        };
      },
      update(data) {
        const queryData = data[Object.keys(data)[0]];
        return ((queryData || {}).sensor || [])[0] || {};
      },
      skip() {
        return !this.sensorId || (!this.selection && !this.selectionInner);
      },
    },
    previewSensorData: {
      query: sensorValues,
      variables() {
        return {
          id: this.sensorId,
          startTime: this.period.dateStart,
          endTime: this.period.dateEnd,
          sampleCount: 250,
          aggregation: this.chartType == 'stepLine' ? 'MAX' : null,
        };
      },
      pollInterval: 20000,
      update(data) {
        const queryData = data[Object.keys(data)[0]];
        return ((queryData || {}).sensor || [])[0] || {};
      },
      skip() {
        return !this.sensorId || !this.period || !this.period.dateStart;
      },

      fetchPolicy: 'cache-and-network',
    },
  },
  data: () => {
    return {
      sensorData: {},
      previewSensorData: {},
      selectionInner: null,
      selectionChangeCounter: 0,
    };
  },

  computed: {
    unitName() {
      return (
        (((this.sensorData || {}).type || {}).measurementUnit || {}).shortName ||
        this.$t('dashboards.operationValuePlot.units')
      );
    },
    chartType() {
      return ((this.sensorData || {}).type || {}).chartType || '';
    },
    values() {
      return this.previewValues.length ? (this.sensorData || {}).values || [] : [];
    },
    previewValues() {
      return (this.previewSensorData || {}).values || [];
    },
    lastValue() {
      return this.previewValues.length
        ? Math.round(this.previewValues[this.previewValues.length - 1].value * 1000) / 1000
        : 0;
    },
    sensorName() {
      return (this.sensorData || {}).name || this.$t('box.value');
    },
    mainSeries() {
      const data = this.prepData(this.values);
      return [
        {
          name: this.sensorName,
          data,
        },
      ];
    },
    previewSeries() {
      const data = this.prepData(this.previewValues);
      return [
        {
          name: this.sensorName,
          data,
        },
      ];
    },
    selection() {
      const min = moment(this.period.dateStart).add(2, 'h').toDate().getTime();
      const max = moment(this.period.dateEnd).add(2, 'h').toDate().getTime();
      if (this.selectionInner && this.selectionInner.max <= max && this.selectionInner.min >= min) {
        return this.selectionInner;
      }
      if (this.previewValues.length) {
        const diff = (max - min) / 4;
        return { min: max - diff, max };
      }
      return {};
    },

    mainOptions() {
      const vm = this;
      return {
        chart: {
          id: 'mainOperValChart',
          type: 'line',
          animations: {
            enabled: false,
          },
          height: '100px!important',
          toolbar: {
            show: true,
            tools: {
              download: false,
              selection: false,
              zoom: true,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: true,
            },
          },
          locales: [ru],
          defaultLocale: 'ru',
        },
        colors: ['rgba(255,99,132,1)'],
        stroke: {
          width: 1,
          curve: 'stepline',
        },
        grid: {
          borderColor: 'rgba(0,0,0,1)',
          xaxis: {
            lines: {
              show: true,
            },
          },
          yaxis: {
            lines: {
              show: true,
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        fill: {
          gradient: {
            enabled: false,
            opacityFrom: 0.5,
            opacityTo: 0,
          },
        },
        tooltip: {
          theme: 'dark',
          x: {
            format: 'dd.MM.yy, HH:mm:ss',
          },
          y: {
            formatter: (v) => vm.formatSensorValue(v),
          },
          enabled: true,
        },
        xaxis: {
          type: 'datetime',
          axisBorder: {
            show: true,
          },
          labels: {
            datetimeUTC: false,
            style: {
              colors: 'grey',
            },
          },
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          tickAmount: 4,
          labels: {
            formatter: (v) => vm.formatNumber(v),
            style: {
              colors: 'grey',
            },
          },
        },
      };
    },
    previewOptions() {
      const min = moment(this.period.dateStart).toDate().getTime();
      const max = moment(this.period.dateEnd).toDate().getTime();
      const vm = this;
      return {
        chart: {
          id: 'previewOperValChart',
          type: 'line',
          animations: {
            enabled: false,
          },
          brush: {
            target: 'mainOperValChart',
            enabled: true,
          },
          locales: [ru],
          defaultLocale: 'ru',
          selection: {
            enabled: true,
            fill: {
              color: 'white',
              opacity: 0.1,
            },
            xaxis: {
              min: vm.selection.min || 0,
              max: vm.selection.max || 0,
            },
          },
          events: {
            selection: function (_, { xaxis }) {
              if (!vm.selectionInner) {
                vm.selectionInner = { min: xaxis.min, max: xaxis.max };
              } else if (xaxis.min != vm.selectionInner.min || xaxis.max != vm.selectionInner.max) {
                const changeCounter = ++vm.selectionChangeCounter;
                setTimeout(() => {
                  if (changeCounter == vm.selectionChangeCounter) {
                    vm.selectionInner = { min: xaxis.min, max: xaxis.max };
                  }
                }, 500);
              }
            },
          },
        },
        colors: ['rgba(255, 206, 86, 1)'],

        stroke: {
          width: 2,
          curve: 'stepline',
        },
        grid: {
          borderColor: 'rgba(0,0,0,0.3)',
          xaxis: {
            lines: {
              show: true,
            },
          },
          yaxis: {
            lines: {
              show: true,
            },
          },
        },
        tooltip: {
          theme: 'dark',
          x: {
            format: 'dd.MM.yy, HH:mm:ss',
          },
          y: {
            formatter: (v) => vm.formatSensorValue(v),
          },
          enabled: true,
        },
        xaxis: {
          type: 'datetime',
          min,
          max,
          tooltip: {
            enabled: false,
          },
          labels: {
            datetimeUTC: false,
            style: {
              colors: 'grey',
            },
          },
        },
        yaxis: {
          labels: {
            show: false,
          },
        },
      };
    },
    loading() {
      return (
        this.$apollo.queries.sensorData.loading || this.$apollo.queries.previewSensorData.loading
      );
    },
  },
  watch: {
    period() {
      this.selectionInner = null;
    },
  },
  methods: {
    prepData(values) {
      const data = values.map((i) => {
        return { x: i.timeStamp, y: i.value };
      });
      return data;
    },
    formatNumber(v) {
      return Number(v.toPrecision(4)).toLocaleString();
    },
    formatSensorValue(v) {
      return `${this.formatNumber(v)} ${this.unitName}`;
    },
  },
};
</script>

<style>
#wrapper {
  position: block;
  background: white;
  border: 1px solid #000;
  box-shadow: 0 22px 35px -16px rgba(0, 0, 0, 0.71);
  height: 200px !important;
  min-height: 200px !important;
  max-height: 300px !important;
  margin: 0 auto;
}
#chart-bar {
  position: relative;
  margin-top: -70px;
}
.dot-opacity-loader {
  position: absolute;
  height: 25px;
  left: calc(50% - 50px);
}
.dot-opacity-loader span {
  background-color: rgba(255, 99, 132, 1);
  margin: 5px 5px;
}
</style>
